// =============================================================================
// PatientQuickSearch
// Buscador de paciente integrado en la cabecera del dashboard.
// Reutiliza patientsApi.search + useDebounce (ya existentes en el proyecto).
// Al seleccionar un resultado navega directamente a /pacientes/:id.
//
// Estilo: pill redondeado (rounded-full), Material Symbols, paleta slate/primary
// (coherente con Sidebar y Ficha 360°). Sin lucide-react.
//
// v1.2.1: placeholder default actualizado a "Buscar por DNI…" para
// comunicar al usuario que solo se busca por DNI.
// =============================================================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientsApi } from '../../api/patients';
import { useDebounce } from '../../hooks/useDebounce';
import type { PatientSearchResult } from '../../types/patient';

const MIN_QUERY_LENGTH = 3;
const MAX_RESULTS = 5;

interface PatientQuickSearchProps {
  placeholder?: string;
  widthClass?: string;
}

export function PatientQuickSearch({
  placeholder = 'Buscar por DNI…',
  widthClass = 'w-64',
}: PatientQuickSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PatientSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounced = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Búsqueda con debounce
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (debounced.trim().length < MIN_QUERY_LENGTH) {
        setResults([]);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const data = await patientsApi.search({ query: debounced.trim() });
        if (!cancelled) {
          setResults(data.slice(0, MAX_RESULTS));
          setIsOpen(true);
          setActiveIndex(-1);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  // Cerrar al clicar fuera
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const goToPatient = (id: string) => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setActiveIndex(-1);
    navigate(`/pacientes/${id}`);
  };

  const goToList = () => {
    const q = query.trim();
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setActiveIndex(-1);
    navigate(q ? `/pacientes?q=${encodeURIComponent(q)}` : '/pacientes');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!isOpen || results.length === 0) {
      if (e.key === 'Enter' && query.trim().length >= MIN_QUERY_LENGTH) {
        e.preventDefault();
        goToList();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = activeIndex >= 0 ? results[activeIndex] : results[0];
      if (target) goToPatient(target.id);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const showDropdown =
    isOpen &&
    query.trim().length >= MIN_QUERY_LENGTH &&
    (isLoading || results.length > 0 || query.trim().length >= MIN_QUERY_LENGTH);

  return (
    <div ref={containerRef} className={`relative ${widthClass}`}>
      <div
        className={`
          relative flex items-center gap-2 px-3 py-1.5
          bg-slate-50 border border-slate-200 rounded-full
          transition-colors duration-200
          focus-within:bg-white focus-within:border-primary/40 focus-within:shadow-sm
        `}
      >
        <span className="material-symbols-outlined text-[18px] text-slate-400">
          search
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (query.trim().length >= MIN_QUERY_LENGTH) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400 min-w-0"
        />
        {query && !isLoading && (
          <button
            type="button"
            onClick={handleClear}
            className="text-slate-400 hover:text-slate-600 flex items-center justify-center"
            title="Limpiar"
          >
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        )}
        {isLoading && (
          <span className="material-symbols-outlined text-[18px] text-slate-400 animate-spin">
            progress_activity
          </span>
        )}
      </div>

      {showDropdown && (
        <div
          className="
            absolute z-30 left-0 right-0 mt-2
            bg-white border border-slate-200 rounded-2xl shadow-lg
            overflow-hidden
          "
        >
          {isLoading && results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-400">Buscando…</div>
          ) : results.length > 0 ? (
            <>
              <ul>
                {results.map((p, idx) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => goToPatient(p.id)}
                      className={`
                        w-full text-left px-4 py-2.5 flex items-center gap-3
                        transition-colors
                        ${idx === activeIndex ? 'bg-primary/5' : 'hover:bg-slate-50'}
                        ${idx < results.length - 1 ? 'border-b border-slate-100' : ''}
                      `}
                    >
                      <span className="w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {getInitials(p.fullName)}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-slate-900 truncate">
                          {p.fullName || 'Paciente sin nombre'}
                        </span>
                        <span className="block text-xs text-slate-500 truncate">
                          {p.documentNumber || '—'}
                          {p.matchedBy ? (
                            <span className="text-slate-400"> · {p.matchedBy}</span>
                          ) : null}
                        </span>
                      </span>
                      <span className="material-symbols-outlined text-[18px] text-slate-300">
                        chevron_right
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={goToList}
                className="w-full px-4 py-2 text-xs text-primary hover:bg-primary/5 border-t border-slate-100 flex items-center justify-center gap-1"
              >
                Ver listado completo
                <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
              </button>
            </>
          ) : (
            <div className="px-4 py-3 text-sm">
              <p className="text-slate-500">Sin coincidencias para "{query.trim()}"</p>
              <button
                type="button"
                onClick={goToList}
                className="mt-1 text-xs text-primary hover:underline"
              >
                Ir al listado completo
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}
